import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import { useAuth } from '../../contexts/AuthContext';
import { fetchKnowledgeCategories, TargetType } from '../../services/learningApi';
import { useApis } from './common';

interface Props {
  targetType: TargetType;
  targetId: string;
  targetName: string;
  /** Entry ids to copy; null = every in-use entry. */
  entryIds: string[] | null;
  /** How many entries will be copied (for the copy text). */
  count: number;
  /** Selected entries that aren't in use (waiting / rejected / stale), to warn about. */
  notInUseCount?: number;
  learnedCategoryId: string | null;
  onClose: () => void;
}

type Result = { material_name: string; category_id: string; category_name: string; snippets: number };

export const ExportKnowledgeDialog: React.FC<Props> = ({
  targetType,
  targetId,
  targetName,
  entryIds,
  count,
  notInUseCount = 0,
  learnedCategoryId,
  onClose,
}) => {
  const apis = useApis();
  const { getIdToken } = useAuth();
  const tokenRef = useRef(getIdToken);
  tokenRef.current = getIdToken;
  const [categories, setCategories] = useState<{ id: string; name: string }[] | null>(null);
  const [mode, setMode] = useState<'existing' | 'new'>('new');
  const [categoryId, setCategoryId] = useState('');
  const [newCategory, setNewCategory] = useState(`${targetName} – curated`);
  const [name, setName] = useState(`${targetName} – learned knowledge (${new Date().toISOString().slice(0, 10)})`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);

  useEffect(() => {
    fetchKnowledgeCategories(() => tokenRef.current())
      .then((cats) => setCategories(cats.filter((c) => c.id !== learnedCategoryId)))
      .catch(() => setCategories([]));
  }, [learnedCategoryId]);

  const canSubmit = name.trim() && (mode === 'existing' ? !!categoryId : !!newCategory.trim());

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await apis.learning.exportKnowledge(targetType, targetId, {
        entry_ids: entryIds,
        name: name.trim(),
        category_id: mode === 'existing' ? categoryId : null,
        new_category_name: mode === 'new' ? newCategory.trim() : null,
      });
      setResult(res);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Export to a Knowledge Base file</DialogTitle>
      <DialogContent dividers>
        {result ? (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              Created “{result.material_name}” with {result.snippets} snippet{result.snippets === 1 ? '' : 's'} in the category “{result.category_name}”.
            </Alert>
            <Typography variant="body2" color="text.secondary" paragraph>
              It's a normal Knowledge Base file now: edit, recategorize or delete it there. To have a chain step or function use it, attach the
              category “{result.category_name}” to that step (chain editor → step → Knowledge categories) or pass it in the call.
            </Typography>
          </>
        ) : (
          <>
            <Alert icon={<AcUnitIcon fontSize="inherit" />} severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                This makes a separate, frozen copy of {count} entr{count === 1 ? 'y' : 'ies'}.
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                <li>
                  <b>Frozen:</b> it won't change as self-improvement keeps learning. Approving, rejecting or editing entries here won't affect it.
                </li>
                <li>
                  <b>Yours:</b> it appears in Knowledge Base as an ordinary file you can edit, move or delete.
                </li>
                <li>
                  <b>Not used automatically:</b> runs only use it where you attach its category. Self-improvement keeps using its own category, so
                  nothing changes for this {targetType}.
                </li>
              </Box>
            </Alert>
            {notInUseCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {notInUseCount} of the selected entries aren't in use (waiting, rejected or stale). They'll be copied as they are.
              </Alert>
            )}
            <TextField fullWidth size="small" label="File name" value={name} onChange={(e) => setName(e.target.value)} sx={{ mb: 2 }} />
            <Typography variant="body2" fontWeight={500}>
              Where should it go?
            </Typography>
            <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as 'existing' | 'new')}>
              <FormControlLabel value="new" control={<Radio size="small" />} label="A new category" />
              {mode === 'new' && (
                <TextField size="small" label="New category name" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} sx={{ ml: 4, mb: 1 }} />
              )}
              <FormControlLabel value="existing" control={<Radio size="small" />} label="An existing category" disabled={categories !== null && categories.length === 0} />
              {mode === 'existing' && (
                <TextField
                  select
                  size="small"
                  label="Category"
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  sx={{ ml: 4, mb: 1, minWidth: 280 }}
                  helperText="The self-improvement category isn't listed: runs already get these entries from it."
                >
                  {categories === null && <MenuItem disabled>Loading…</MenuItem>}
                  {categories?.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </RadioGroup>
            {error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {result ? (
          <>
            <Button component={RouterLink} to={`/dashboard/materials?category=${result.category_id}`}>
              Open in Knowledge Base
            </Button>
            <Button variant="contained" onClick={onClose}>
              Done
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button variant="contained" onClick={submit} disabled={!canSubmit || busy} startIcon={busy ? <CircularProgress size={14} /> : undefined}>
              Create frozen copy
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
